import { Link, CardTable } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  WalletAddress,
  CurrencyValue,
  TableSkeleton,
  RelativeDateTime
} from '@blockanalitica/ui';
import { useApi, PaginatedResponse } from '@/api';
import { useParams } from 'react-router';
import { useNetwork } from '@/hooks';

type WalletsData = {
  address: string;
  wallet_address: string;
  market_address: string;
  market_underlying_address: string;
  name: string;
  market_symbol: string;
  collaterals: { symbol: string; address: string }[];
  collateral: string | null;
  supply: string | null;
  health_rate: string | null;
  drop: string | null;
  first_activity: string | null;
  last_activity: string | null;
};

export default function SuppliersWalletsTable({ search }: { search: string }) {
  const network = useNetwork();
  const { marketAddress } = useParams();
  const { pagination, sorting } = useTableProps({
    pageSize: 15,
    initialSorting: '-supply'
  });

  const [data, error] = useApi<PaginatedResponse<WalletsData[]>>({
    url: `/markets/${marketAddress}/wallets/`,
    queryParams: {
      type: 'supplier',
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      search: search
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} />;
  }
  const columns: TableColumn<WalletsData>[] = [
    {
      header: 'Wallet Address',
      getCell: (row) => <WalletAddress address={row.wallet_address} />
    },
    {
      header: 'Supply',
      getCell: (row) => (
        <CurrencyValue
          value={row.supply}
          options={{
            currency: {
              name: row.market_symbol,
              address: row.market_underlying_address,
              network: network
            }
          }}
        />
      ),
      sortingField: 'supply',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Last Activity',
      getCell: (row) => (
        <>
          {row.last_activity ? (
            <RelativeDateTime
              value={row.last_activity}
              formatDateTimeOptions={{ toRelativeUnit: 'days' }}
            />
          ) : null}
        </>
      ),
      sortingField: 'last_activity',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <CardTable<WalletsData>
      rows={data['results']}
      columns={columns}
      getKey={(row) => row.wallet_address}
      rowLinkProps={{
        to: (row) => row.wallet_address,
        // @ts-ignore
        linkComponent: Link
      }}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  );
}
